// ---------------------------------------------------------------------------
// Oracle bind parameter utilities
// Detects :param_name tokens in SQL (skipping string literals) and
// substitutes them with user-supplied values before execution.
// ---------------------------------------------------------------------------

/**
 * Parse SQL and return ordered, unique list of bind parameter names.
 * Skips content inside string literals and SQL comments.
 *
 * Example: "WHERE person_number = :pnum AND name = :pname"
 *   → ['pnum', 'pname']
 */
export function detectBindParams(sql) {
  const source = String(sql || '');
  const params = [];
  const seen = new Set();
  let inSingle = false;
  let inDouble = false;
  let inLineComment = false;
  let inBlockComment = false;
  let i = 0;

  while (i < source.length) {
    const ch = source[i];
    const next = source[i + 1];

    if (inLineComment) {
      if (ch === '\n' || ch === '\r') inLineComment = false;
      i++;
      continue;
    }

    if (inBlockComment) {
      if (ch === '*' && next === '/') {
        inBlockComment = false;
        i += 2;
      } else {
        i++;
      }
      continue;
    }

    if (inSingle) {
      if (ch === "'") {
        if (next === "'") {
          i += 2; // escaped single quote inside string
        } else {
          inSingle = false;
          i++;
        }
      } else {
        i++;
      }
      continue;
    }

    if (inDouble) {
      if (ch === '"') {
        if (next === '"') {
          i += 2; // escaped quote inside quoted identifier
        } else {
          inDouble = false;
          i++;
        }
      } else {
        i++;
      }
      continue;
    }

    if (ch === '-' && next === '-') {
      inLineComment = true;
      i += 2;
      continue;
    }

    if (ch === '/' && next === '*') {
      inBlockComment = true;
      i += 2;
      continue;
    }

    if (ch === "'") {
      inSingle = true;
      i++;
      continue;
    }

    if (ch === '"') {
      inDouble = true;
      i++;
      continue;
    }

    if (ch === ':') {
      const m = source.slice(i).match(/^:[A-Za-z_][A-Za-z0-9_]*/);
      if (m) {
        const name = m[0].slice(1); // strip leading colon
        if (!seen.has(name)) {
          seen.add(name);
          params.push(name);
        }
        i += m[0].length;
        continue;
      }
    }

    i++;
  }

  return params;
}

// SQL keywords and functions that should never be quoted
const UNQUOTED_KEYWORDS = new Set([
  'NULL', 'TRUE', 'FALSE',
  'SYSDATE', 'SYSTIMESTAMP', 'CURRENT_DATE', 'CURRENT_TIMESTAMP',
]);

/**
 * Decide whether a raw user-entered value needs single-quote wrapping.
 *
 * Rules (in order):
 *  1. Already quoted → use as-is
 *  2. Pure number (integer or decimal) → use as-is
 *  3. SQL keyword/constant (NULL, SYSDATE, …) → use as-is
 *  4. SQL expression — contains ( or starts with a known function → use as-is
 *  5. Everything else (dates, strings, etc.) → wrap in single quotes
 */
function quoteValue(raw) {
  const trimmed = raw.trim();

  // Already single-quoted
  if (trimmed.startsWith("'") && trimmed.endsWith("'") && trimmed.length >= 2) return trimmed;

  // Pure number
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed;

  // SQL keyword / constant
  if (UNQUOTED_KEYWORDS.has(trimmed.toUpperCase())) return trimmed;

  // SQL expression — has parentheses (function call, subquery, etc.)
  if (trimmed.includes('(')) return trimmed;

  // Anything else: date literals, plain strings — wrap in single quotes
  // Escape any single quotes inside the value
  return "'" + trimmed.replace(/'/g, "''") + "'";
}

/**
 * Replace all :param_name occurrences in SQL with the provided values.
 * Sort by name length descending to prevent :a from matching inside :ab.
 *
 * @param {string} sql
 * @param {Object.<string,string>} values  e.g. { pnum: '12345', pname: 'Smith' }
 * @returns {string}
 */
export function applyBindParams(sql, values) {
  const source = String(sql || '');
  const names = Object.keys(values || {});
  if (names.length === 0) return source;

  let out = '';
  let inSingle = false;
  let inDouble = false;
  let inLineComment = false;
  let inBlockComment = false;

  for (let i = 0; i < source.length; i++) {
    const ch = source[i];
    const next = source[i + 1];

    if (inLineComment) {
      out += ch;
      if (ch === '\n' || ch === '\r') inLineComment = false;
      continue;
    }

    if (inBlockComment) {
      out += ch;
      if (ch === '*' && next === '/') {
        out += next;
        inBlockComment = false;
        i++;
      }
      continue;
    }

    if (inSingle) {
      out += ch;
      if (ch === "'") {
        if (next === "'") {
          out += next;
          i++;
        } else {
          inSingle = false;
        }
      }
      continue;
    }

    if (inDouble) {
      out += ch;
      if (ch === '"') {
        if (next === '"') {
          out += next;
          i++;
        } else {
          inDouble = false;
        }
      }
      continue;
    }

    if (ch === '-' && next === '-') {
      out += ch + next;
      inLineComment = true;
      i++;
      continue;
    }

    if (ch === '/' && next === '*') {
      out += ch + next;
      inBlockComment = true;
      i++;
      continue;
    }

    if (ch === "'") {
      out += ch;
      inSingle = true;
      continue;
    }

    if (ch === '"') {
      out += ch;
      inDouble = true;
      continue;
    }

    if (ch === ':') {
      const m = source.slice(i).match(/^:[A-Za-z_][A-Za-z0-9_]*/);
      if (m) {
        const name = m[0].slice(1);
        if (Object.prototype.hasOwnProperty.call(values, name)) {
          out += quoteValue(values[name]);
          i += m[0].length - 1;
          continue;
        }
      }
    }

    out += ch;
  }

  return out;
}
